import { useMemo, useState } from 'react';
import { Autocomplete, Checkbox, createFilterOptions, ListItem, TextField, } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import type { FilterSize } from './filterSize';
import { DEFAULT_FILTER_SIZE } from './filterSize';
import { getCountedLabel } from './filterLabel';

const SELECT_ALL_VALUE = '__select-all__';
const SELECT_ALL_LABEL = 'Select all';

interface OptionEntry {
  value: string;
  label: string;
}

interface MultiAutocompleteFilterProps {
  label: string;
  value: string[];
  options: string[];
  onChange: (val: string[]) => void;
  placeholder?: string;
  minWidth?: number;
  size?: FilterSize;
  /** When true, prepend a "Select all" row that toggles every option. */
  allSelectable?: boolean;
}

const filter = createFilterOptions<OptionEntry>();

const MultiAutocompleteFilter = ({
  label,
  value,
  options,
  onChange,
  placeholder = 'Search...',
  minWidth = 180,
  size = DEFAULT_FILTER_SIZE,
  allSelectable = true,
}: MultiAutocompleteFilterProps) => {
  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);

  const summary = value.length > 0 ? getCountedLabel(label, value.length) : '';
  const inputValue = focused ? search : summary;

  const optionEntries = useMemo<OptionEntry[]>(
    () => options.map((opt) => ({ value: opt, label: opt })),
    [options],
  );

  const selectedEntries = useMemo<OptionEntry[]>(
    () => optionEntries.filter((o) => value.includes(o.value)),
    [optionEntries, value],
  );

  const allSelected =
    optionEntries.length > 0 && optionEntries.length === selectedEntries.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(optionEntries.map((o) => o.value));
    }
  };

  return (
    <Autocomplete<OptionEntry, true, false, false>
      multiple
      disableCloseOnSelect
      size={size}
      options={optionEntries}
      value={selectedEntries}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      getOptionLabel={(o) => o.label}
      onChange={(_e, next, reason) => {
        if (reason === 'clear') {
          onChange([]);
          return;
        }
        if (next.some((o) => o.value === SELECT_ALL_VALUE)) {
          handleSelectAll();
          return;
        }
        onChange(next.map((o) => o.value));
      }}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        if (allSelectable && optionEntries.length > 0) {
          return [{ value: SELECT_ALL_VALUE, label: SELECT_ALL_LABEL }, ...filtered];
        }
        return filtered;
      }}
      renderOption={(props, option, state) => {
        const { key, ...otherProps } = props as typeof props & { key?: string };
        const isSelectAll = option.value === SELECT_ALL_VALUE;
        const checked = isSelectAll ? allSelected : state.selected;
        return (
          <ListItem key={key ?? option.value} sx={{px: .25}} {...otherProps}>
            <Checkbox
              size="small"
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              checked={checked}
            />
            {option.label}
          </ListItem>
        );
      }}
      inputValue={inputValue}
      onInputChange={(_e, next, reason) => {
        if (reason === 'input') setSearch(next);
        if (reason === 'reset') setSearch('');
      }}
      renderTags={() => null}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          onFocus={() => { setFocused(true); }}
          onBlur={() => { setFocused(false); setSearch(''); }}
        />
      )}
      sx={{ minWidth }}
    />
  );
};

export default MultiAutocompleteFilter;
